/**
 * The sidebar, footer and mobile tabs — hand-off README "Navigation", in that order and those
 * words. Declared in code (Rule 1: routes and navigation live in the app that owns them), never
 * from page manifests. Icons are the design's (designs/app/Shell.dc.html).
 */
import {
  Activity, ArrowRightLeft, Brain, Gem, GitBranch, HeartPulse, History, House, Inbox, LibraryBig,
  LifeBuoy, Menu, PackageOpen, Plug, Route, Settings2, ShieldAlert, Trophy, Workflow,
} from 'lucide-react';
import type { ShellNavGroup, ShellNavItem, ShellTab } from '@inferrex/ui';

/** Every navigable area. A route names the area it lives in, which lights up its nav entry. */
export type NavKey =
  | 'home' | 'review'
  | 'systems' | 'library'
  | 'mappings' | 'pipelines' | 'workflows'
  | 'records' | 'gamification'
  | 'health' | 'repairs'
  | 'insights' | 'governance'
  | 'protect' | 'archive' | 'migrations'
  | 'settings' | 'help'
  | 'none';

export const NAV_GROUPS: readonly ShellNavGroup[] = [
  { key: 'top', items: [
    { key: 'home', label: 'Home', icon: House, to: '/' },
    { key: 'review', label: 'Review queue', icon: Inbox, to: '/review' },
  ] },
  { key: 'connect', label: 'Connect', items: [
    { key: 'systems', label: 'Systems', icon: Plug, to: '/systems' },
    { key: 'library', label: 'Connector library', icon: LibraryBig, to: '/library' },
  ] },
  { key: 'map-and-sync', label: 'Map and sync', items: [
    { key: 'mappings', label: 'Mappings', icon: ArrowRightLeft, to: '/mappings' },
    { key: 'pipelines', label: 'Pipelines', icon: Workflow, to: '/pipelines' },
    { key: 'workflows', label: 'Workflows', icon: GitBranch, to: '/workflows' },
  ] },
  { key: 'records-and-engagement', label: 'Records and engagement', items: [
    { key: 'records', label: 'Golden records', icon: Gem, to: '/records' },
    { key: 'gamification', label: 'Gamification', icon: Trophy, to: '/gamification' },
  ] },
  { key: 'watch', label: 'Watch', items: [
    { key: 'health', label: 'Health and alerts', icon: Activity, to: '/health' },
    { key: 'repairs', label: 'Repairs', icon: HeartPulse, to: '/repairs' },
  ] },
  { key: 'understand', label: 'Understand', items: [
    { key: 'insights', label: 'Stack insights', icon: Brain, to: '/insights' },
    { key: 'governance', label: 'Sensitive data', icon: ShieldAlert, to: '/governance' },
  ] },
  { key: 'protect', label: 'Protect', items: [
    { key: 'protect', label: 'Restore and retention', icon: History, to: '/protect' },
    { key: 'archive', label: 'Archive', icon: PackageOpen, to: '/archive' },
    { key: 'migrations', label: 'Migrations', icon: Route, to: '/migrations' },
  ] },
];

export const FOOTER_NAV: readonly ShellNavItem[] = [
  { key: 'settings', label: 'Workspace settings', icon: Settings2, to: '/settings' },
  { key: 'help', label: 'Help and support', icon: LifeBuoy, to: '/settings/portal' },
];

/** Mobile: Home, Review, Systems, Pipelines, More. "More" opens the full navigation. */
export const MOBILE_TABS: readonly ShellTab[] = [
  { key: 'home', label: 'Home', icon: House, to: '/' },
  { key: 'review', label: 'Review', icon: Inbox, to: '/review' },
  { key: 'systems', label: 'Systems', icon: Plug, to: '/systems' },
  { key: 'pipelines', label: 'Pipelines', icon: Workflow, to: '/pipelines' },
  { key: 'more', label: 'More', icon: Menu },
];

/** The nav entries a mobile tab stands for; anything else lights up "More". */
const TAB_KEYS = new Set(['home', 'review', 'systems', 'pipelines']);
export function mobileTabFor(nav: NavKey): string {
  return TAB_KEYS.has(nav) ? nav : 'more';
}
